import React, { useCallback, useState, useMemo, useEffect } from 'react';
import { ArrowLeft } from 'react-feather';
import { InstantSearch, Configure, Index } from 'react-instantsearch/dom';
import { isEqual, get, isEmpty, times } from 'lodash';
import { getQueryParam, setParamsFromObject } from 'utils/url';

import { routes } from 'constants/config';
import SearchBox from 'components/shared/SearchBox';
import Text, { TEXT_VARIANTS } from 'components/ui/Typography/Text';
import { useFetch } from 'components/shared/hooks';
import * as INDEX_KEYS from 'components/Learn/Search/constants/indexKeys';
import AdvancedSearchTitleResults from './AdvancedSearchTitleResults';
import AdvancedSearchFilters from './AdvancedSearchFilters';
import SearchConfigProvider from './SearchConfigProvider';
import NoResults from './NoResults';
import AdvancedSearchTabs from './AdvancedSearchTabs';
import AdvancedSearchContentResults from './AdvancedSearchContentResults';
import AdvancedSearchResultCardLoading from './AdvancedSearchResultCardLoading';

const TAB_NAMES = {
  TITLE: 'title',
  CONTENT: 'content',
};

const STATES = {
  NEW_VERSION_PENDING: 'new_version_pending',
  PUBLISHED: 'published',
  DRAFT: 'draft',
};

const CONTENT_TYPES = {
  LESSON: 'lesson',
  DRAFT_LESSON: 'draft_lesson',
};

function AdvancedContentSearchRoot() {
  const [searchState, setSearchState] = useState({});
  const queryParam = getQueryParam(window.location.href, 'query');
  const tabParam = getQueryParam(window.location.href, 'tab');
  const [query, setQuery] = useState(queryParam || '');
  const [filters, setFilters] = useState();
  const [facetFilters, setFacetFilters] = useState();
  const [numericFilters, setNumericFilters] = useState();
  const [activeTab, setActiveTab] = useState(tabParam || TAB_NAMES.TITLE);

  const { data = {} } = useFetch(routes.CONTENT.SEARCH);
  const { search } = data;

  const onQueryChange = value => {
    const newFilters = { ...filters, query: value };
    window.history.replaceState(
      {},
      null,
      setParamsFromObject(routes.CONTENT.SEARCH, newFilters),
    );
    setQuery(value);
  };

  const onSearchStateChange = state => {
    const nextSearchState = { ...state };
    nextSearchState.indices = get(nextSearchState, 'indices', {});
    // Since our SearchBox (the search input) lives outside the InstantSearch
    // context, updating the search query will not reset the pages of each index
    // if the "Show More" button is clicked. Therefore we would have to reset
    // them manually when the search state changes.
    if (
      !isEmpty(searchState) &&
      !isEqual(state.configure, searchState.configure)
    ) {
      Object.keys(nextSearchState.indices).forEach(indexName => {
        Object.assign(nextSearchState.indices[indexName], { page: 1 });
      });
    }
    setSearchState({ ...nextSearchState });
  };

  const [searchLessonPathsIndexCount, setSearchLessonPathsIndexCount] =
    useState(null);
  const [searchSectionElementsIndexCount, setSectionElementsIndexCount] =
    useState(null);

  useEffect(() => {
    if (searchLessonPathsIndexCount === 0) setActiveTab(TAB_NAMES.CONTENT);
    if (searchSectionElementsIndexCount === 0) setActiveTab(TAB_NAMES.TITLE);
  }, [searchLessonPathsIndexCount, searchSectionElementsIndexCount]);

  const searchCount = useMemo(() => {
    return searchLessonPathsIndexCount !== null
      ? searchLessonPathsIndexCount + searchSectionElementsIndexCount
      : null;
  }, [searchLessonPathsIndexCount, searchSectionElementsIndexCount]);

  const isLoading = searchCount === null;
  const algoliaFilter = useCallback(
    incomingFilters => {
      const facetedFilter = [];
      // Restricting all duplicate draft lesson results
      facetedFilter.push([
        `state:-${STATES.NEW_VERSION_PENDING}`,
        `content_type:-${CONTENT_TYPES.DRAFT_LESSON}`,
      ]);
      facetedFilter.push([
        `state:-${STATES.DRAFT}`,
        `content_type:-${CONTENT_TYPES.LESSON}`,
      ]);

      if (incomingFilters?.contentType) {
        const contentType = [
          `content_type:${incomingFilters?.contentType}`,
          `belongs_to_content_type:${incomingFilters?.contentType}`,
        ];
        if (incomingFilters?.contentType === CONTENT_TYPES.LESSON) {
          contentType.push(
            `content_type:${CONTENT_TYPES.DRAFT_LESSON}`,
            `belongs_to_content_type:${CONTENT_TYPES.DRAFT_LESSON}`,
          );
        }
        facetedFilter.push(contentType);
      }

      if (incomingFilters?.status) {
        const state = [];
        state.push(`state:${incomingFilters?.status}`);
        state.push(`state:${STATES.NEW_VERSION_PENDING}`);
        if (query !== '' && incomingFilters?.status === STATES.PUBLISHED) {
          facetedFilter.push(
            [`content_type:-${CONTENT_TYPES.DRAFT_LESSON}`],
            [`belongs_to_content_type:-${CONTENT_TYPES.DRAFT_LESSON}`],
          );
        } else if (query !== '' && incomingFilters?.status === STATES.DRAFT) {
          facetedFilter.push(
            [`content_type:-${CONTENT_TYPES.LESSON}`],
            [`belongs_to_content_type:-${CONTENT_TYPES.LESSON}`],
          );
        }
        facetedFilter.push(state);
      }

      if (incomingFilters.tags) {
        incomingFilters.tags.forEach(tag => {
          facetedFilter.push(`tags:${tag}`);
        });
      }
      const numericFilter = [];
      if (
        incomingFilters.workspace &&
        incomingFilters.workspace !== 'default'
      ) {
        numericFilter.push(`workspace_id=${incomingFilters?.workspace}`);
      }
      if (incomingFilters.startDate) {
        const startDate = new Date(incomingFilters.startDate).getTime() / 1000;
        numericFilter.push(`modified_at >= ${startDate}`);
      }
      if (incomingFilters.endDate) {
        const endDateObject = new Date(incomingFilters.endDate);
        endDateObject.setDate(endDateObject.getDate() + 1);
        const endDate = endDateObject.getTime() / 1000;
        numericFilter.push(`modified_at <= ${endDate}`);
      }
      if (incomingFilters.creator) {
        numericFilter.push(`creator_id=${incomingFilters?.creator}`);
      }
      setFilters(incomingFilters);
      setFacetFilters(facetedFilter);

      setNumericFilters(numericFilter);
    },
    [query],
  );

  return (
    <>
      <div className="AdvancedContentSearchRoot mt-24 ff-ss pb-40">
        <div className="d-ib">
          <a className="d-f fd-r ai-c" href={routes.CONTENT.INDEX}>
            <ArrowLeft className="mr-12 c-link" size={24} />
            <Text variant={TEXT_VARIANTS.BODY_LINK}>
              {t('contents.advanced_search.back_to_content')}
            </Text>
          </a>
        </div>
        <div className="AdvancedContentSearchRoot-search-container w-fill bg-navy-dark mt-16 d-f ai-c jc-c bdrs-top">
          <SearchBox
            className="AdvancedContentSearchRoot-search-box"
            query={query}
            placeholder={t('contents.advanced_search.search_placeholder')}
            onChange={e => onQueryChange(e.target.value)}
            onClear={() => onQueryChange('')}
            disabled={false}
            useFeatherIcon
          />
        </div>
        <div className="mt-16">
          <Text variant={TEXT_VARIANTS.BODY_BOLD}>
            {t('contents.advanced_search.filter')}
          </Text>
        </div>
        <div className="mt-16">
          <AdvancedSearchFilters
            filtersChanged={algoliaFilter}
            query={query}
            creators={data?.creators}
            currentUserId={data?.current_user_id}
          />
        </div>
        {query && isLoading && (
          <>
            <div className="mt-24 mb-24">
              <Text variant={TEXT_VARIANTS.TITLE_BOLD}>
                {t('common.loading')}
              </Text>
            </div>
            <AdvancedSearchTabs
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              titleCount={null}
              contentCount={null}
              isLoading
            />
          </>
        )}
        {isLoading && (
          <div className="AdvancedSearchResults fw-w">
            {times(10, i => (
              <AdvancedSearchResultCardLoading key={i} />
            ))}
          </div>
        )}
        {search && (
          <SearchConfigProvider config={search}>
            {({ credentialsProps, indexes }) => (
              <InstantSearch
                {...credentialsProps}
                searchState={searchState}
                onSearchStateChange={onSearchStateChange}
              >
                <Configure
                  query={query}
                  hitsPerPage={20}
                  snippetEllipsisText="..."
                  facetFilters={facetFilters}
                  numericFilters={numericFilters}
                />
                {searchCount === 0 && <NoResults query={query} />}
                {query && searchCount !== 0 && !isLoading && (
                  <>
                    <div className="mt-24 mb-24">
                      <Text
                        variant={TEXT_VARIANTS.TITLE_BOLD}
                      >{`"${query}"`}</Text>
                      <Text variant={TEXT_VARIANTS.SUBHEADER}>
                        {t('contents.advanced_search.result_count', {
                          localeNumber: searchCount.toLocaleString(I18n.locale),
                          count: searchCount,
                        })}
                      </Text>
                    </div>
                    <AdvancedSearchTabs
                      activeTab={activeTab}
                      setActiveTab={setActiveTab}
                      titleCount={searchLessonPathsIndexCount}
                      contentCount={searchSectionElementsIndexCount}
                    />
                  </>
                )}
                <Index indexName={indexes[INDEX_KEYS.LESSONS_PATHS]}>
                  <AdvancedSearchTitleResults
                    isActive={activeTab === TAB_NAMES.TITLE || !query}
                    nbHitsChange={setSearchLessonPathsIndexCount}
                    editableLessons={data?.editableLessons}
                  />
                </Index>
                <Index indexName={indexes[INDEX_KEYS.SECTIONS_ELEMENTS]}>
                  <AdvancedSearchContentResults
                    isActive={activeTab === TAB_NAMES.CONTENT && query !== ''}
                    nbHitsChange={setSectionElementsIndexCount}
                    editableLessons={data?.editableLessons}
                  />
                </Index>
              </InstantSearch>
            )}
          </SearchConfigProvider>
        )}
      </div>
    </>
  );
}

export default props => <AdvancedContentSearchRoot {...props} />;
